import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { epicsApi, type Epic } from '../epics/epicsApi';
import RightPanel from '../components/RightPanel';
import RichTextEditor from '../components/RichTextEditor';
import { useIsMobile } from '../hooks/useIsMobile';

export default function EpicsPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const { getToken, refresh } = useAuth();
  const isMobile = useIsMobile();
  const [epics, setEpics] = useState<Epic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newEpicName, setNewEpicName] = useState('');
  const [newEpicDescription, setNewEpicDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const loadEpics = useCallback(() => {
    setLoading(true);
    epicsApi
      .list(workspaceId, getToken, refresh)
      .then(data => setEpics(data.epics))
      .catch(() => setError('Failed to load epics'))
      .finally(() => setLoading(false));
  }, [workspaceId, getToken, refresh]);

  useEffect(() => {
    loadEpics();
  }, [loadEpics]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newEpicName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await epicsApi.create(workspaceId, newEpicName.trim(), newEpicDescription.trim(), getToken, refresh);
      setNewEpicName('');
      setNewEpicDescription('');
      setShowCreateForm(false);
      loadEpics();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create epic');
    } finally {
      setCreating(false);
    }
  }

  return (
    <main className="page" style={{ marginRight: !isMobile && showCreateForm ? 456 : undefined }}>
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <h1>Epics</h1>
        <button type="button" onClick={() => setShowCreateForm(true)}>
          + Create epic
        </button>
      </div>

      {showCreateForm && (
        <RightPanel title="Create Epic" onClose={() => setShowCreateForm(false)}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {epics.length === 0 && (
              <div style={{ marginBottom: 4 }}>
                <strong>Create your first epic</strong>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
                  Epics are big themes or goals — things like "Launch v1" or "Q3 Marketing Push."
                  Each epic holds one or more projects, which in turn hold tasks.
                </p>
              </div>
            )}
            <input
              value={newEpicName}
              onChange={e => setNewEpicName(e.target.value)}
              placeholder="Epic name"
              required
              autoFocus
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span>Description</span>
              <RichTextEditor value={newEpicDescription} onChange={setNewEpicDescription} />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create epic'}</button>
              <button type="button" onClick={() => setShowCreateForm(false)}>Cancel</button>
            </div>
          </form>
        </RightPanel>
      )}

      {loading && <p className="muted">Loading…</p>}
      {error && <p className="error">{error}</p>}
      {!loading && epics.length === 0 && <p className="muted">No epics yet.</p>}

      <ul className="card-list">
        {epics.map(epic => (
          <li key={epic.epicId} className="card card-clickable">
            <Link to={`/workspaces/${workspaceId}/epics/${epic.epicId}`} className="card-link-block">
              <strong className="card-link">{epic.name}</strong>
              <p className="muted" style={{ margin: '4px 0 0' }}>
                {epic.status} {epic.description && `— ${epic.description}`}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
